<template>
  <main class="usage-analytics" :aria-busy="loading ? 'true' : 'false'">
    <header class="usage-analytics__header">
      <div class="usage-analytics__heading">
      <p class="usage-analytics__breadcrumb">Analíticas <span>/</span> Uso de la plataforma</p>
        <h1>Analíticas de uso</h1>
        <p class="usage-analytics__subtitle">
          Consulta los ingresos, la actividad por apartado, la interacción con los comunicados y el tiempo estimado de miembros y submiembros.
        </p>
      </div>

      <form class="usage-analytics__filters" @submit.prevent="fetchSummary">
        <label class="usage-analytics__preset-field" for="usage-range-preset">
          <span>Periodo</span>
          <select
            id="usage-range-preset"
            v-model="rangePreset"
            :disabled="loading"
            @change="selectPreset"
          >
            <option v-if="maxRangeDays >= 7" value="7">Últimos 7 días</option>
            <option v-if="maxRangeDays >= 30" value="30">Últimos 30 días</option>
            <option v-if="maxRangeDays >= 90" value="90">Últimos 90 días</option>
            <option value="custom">Elegir fechas</option>
          </select>
        </label>
        <label class="usage-analytics__date-field" for="usage-start-date">
          <span>Desde</span>
          <input
            id="usage-start-date"
            v-model="startDate"
            type="date"
            :max="endDate || todayInBogota"
            :disabled="loading"
            @input="rangePreset = 'custom'"
          >
        </label>
        <label class="usage-analytics__date-field" for="usage-end-date">
          <span>Hasta</span>
          <input
            id="usage-end-date"
            v-model="endDate"
            type="date"
            :min="startDate || undefined"
            :max="todayInBogota"
            :disabled="loading"
            @input="rangePreset = 'custom'"
          >
        </label>
        <button
          class="usage-analytics__button usage-analytics__button--primary"
          type="submit"
          :disabled="dateRangeInvalid || dateRangeTooLong || loading"
        >
          Aplicar
        </button>
        <button
          class="usage-analytics__button usage-analytics__button--secondary usage-analytics__export-button"
          type="button"
          :disabled="!canExportCsv"
          @click="exportCsv"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M5 17v3h14v-3"></path></svg>
          Exportar
        </button>
      </form>
    </header>

    <p v-if="dateRangeInvalid || dateRangeTooLong" class="usage-analytics__validation" role="alert">
      {{ dateRangeInvalid ? 'Selecciona un rango de fechas válido.' : 'El periodo supera el máximo de ' + maxRangeDays + ' días.' }}
    </p>
    <div v-if="exportError" class="usage-analytics__feedback usage-analytics__feedback--error" role="alert">
      {{ exportError }}
    </div>

    <div v-if="loading && !summary" class="usage-analytics__loading" role="status">
      <span class="usage-analytics__spinner" aria-hidden="true"></span>
      Cargando analíticas…
    </div>

    <div v-if="error" class="usage-analytics__feedback usage-analytics__feedback--error" role="alert">
      <span>{{ error }}</span>
      <button class="usage-analytics__retry" type="button" :disabled="loading" @click="fetchSummary">
        Reintentar
      </button>
    </div>

    <section v-if="summary" class="usage-analytics__metrics" aria-label="Indicadores de miembros y submiembros">
      <article v-for="metric in metrics" :key="metric.label" class="usage-metric">
        <div class="usage-metric__icon" aria-hidden="true">
          <svg v-if="metric.icon === 'members'" viewBox="0 0 24 24">
            <circle cx="9" cy="8" r="3"></circle>
            <path d="M3.5 19a5.5 5.5 0 0 1 11 0"></path>
            <path d="M16 5.5a3 3 0 0 1 0 5.8M17 14a5 5 0 0 1 3.5 4.8"></path>
          </svg>
          <svg v-else-if="metric.icon === 'unique-login'" viewBox="0 0 24 24">
            <circle cx="9" cy="8" r="3"></circle>
            <path d="M3.5 19a5.5 5.5 0 0 1 11 0"></path>
            <path d="m15.5 12.5 2 2 3.5-4"></path>
          </svg>
          <svg v-else-if="metric.icon === 'logins'" viewBox="0 0 24 24">
            <path d="M10 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5"></path>
            <path d="M13 16l4-4-4-4M17 12H8"></path>
          </svg>
          <svg v-else viewBox="0 0 24 24">
            <path d="M4 18.5A9 9 0 1 1 20 18.5"></path>
            <path d="M12 12l4-4M6 19h12"></path>
            <circle cx="12" cy="12" r="1"></circle>
          </svg>
        </div>
        <div class="usage-metric__content">
          <p class="usage-metric__label">{{ metric.label }}</p>
          <strong class="usage-metric__value">{{ metric.value }}</strong>
          <p class="usage-metric__caption">{{ metric.caption }}</p>
          <div v-if="metric.secondary" class="usage-metric__secondary">
            <p>
              <span>{{ metric.secondary.label }}</span>
              <strong>{{ metric.secondary.value }}</strong>
            </p>
            <p v-if="metric.secondary.caption">{{ metric.secondary.caption }}</p>
          </div>
          <p v-if="metric.comparison" class="usage-metric__comparison">
            {{ metric.comparison }}
          </p>
        </div>
      </article>
    </section>

    <section v-if="summary" class="usage-analytics__charts" aria-label="Actividad de miembros y submiembros">
      <article class="usage-analytics__chart-card usage-analytics__chart-card--trend">
        <div class="usage-analytics__chart-heading">
          <div>
            <p class="usage-analytics__section-label">Ingresos a la plataforma</p>
            <h2>Ingresos por día</h2>
            <p class="usage-analytics__chart-subtitle">
              Compara los ingresos totales con el total de miembros y submiembros que ingresaron cada día.
            </p>
          </div>
          <div class="usage-analytics__trend-navigation">
            <span class="usage-analytics__trend-period">{{ trendPeriodLabel }}</span>
            <button
              class="usage-analytics__page-button"
              type="button"
              :disabled="!canShowOlderTrendPage"
              aria-label="Ver días anteriores"
              title="Ver días anteriores"
              @click="showOlderTrendPage"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14.5 5-7 7 7 7"></path></svg>
            </button>
            <button
              class="usage-analytics__page-button"
              type="button"
              :disabled="!canShowNewerTrendPage"
              aria-label="Ver días posteriores"
              title="Ver días posteriores"
              @click="showNewerTrendPage"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9.5 5 7 7-7 7"></path></svg>
            </button>
          </div>
        </div>
        <div class="usage-analytics__legend" aria-label="Referencias de la gráfica">
          <span><i class="usage-analytics__legend-dot usage-analytics__legend-dot--logins"></i>Ingresos</span>
          <span><i class="usage-analytics__legend-dot usage-analytics__legend-dot--members"></i>Total de miembros y submiembros que ingresaron</span>
        </div>

        <div v-if="trendChart.hasActivity" class="usage-analytics__trend-chart">
          <svg viewBox="0 0 720 278" role="group" aria-label="Barras diarias de ingresos y personas que ingresaron">
            <g v-for="guide in trendChart.guides" :key="`guide-${guide.value}`">
              <line x1="42" x2="704" :y1="guide.y" :y2="guide.y" class="usage-analytics__chart-gridline"></line>
              <text x="34" :y="guide.y + 4" text-anchor="end" class="usage-analytics__chart-axis-label">
                {{ guide.label }}
              </text>
            </g>
            <g v-for="bar in trendChart.bars" :key="`bars-${bar.date}`">
              <rect
                :x="bar.loginsX"
                :y="bar.loginsY"
                :width="bar.width"
                :height="bar.loginsHeight"
                rx="3"
                class="usage-analytics__bar usage-analytics__bar--logins"
                role="img"
                :tabindex="bar.totalLogins > 0 ? 0 : -1"
                :aria-label="trendBarAriaLabel(bar, 'logins')"
                :aria-describedby="trendTooltip && trendTooltip.date === bar.date && trendTooltip.kind === 'logins' ? 'usage-trend-tooltip' : null"
                @mouseenter="showTrendTooltip($event, bar, 'logins')"
                @mousemove="showTrendTooltip($event, bar, 'logins')"
                @mouseleave="hideTrendTooltip"
                @focus="showTrendTooltip($event, bar, 'logins')"
                @blur="hideTrendTooltip"
              >
              </rect>
              <rect
                :x="bar.membersX"
                :y="bar.membersY"
                :width="bar.width"
                :height="bar.membersHeight"
                rx="3"
                class="usage-analytics__bar usage-analytics__bar--members"
                role="img"
                :tabindex="bar.membersLoggedIn > 0 ? 0 : -1"
                :aria-label="trendBarAriaLabel(bar, 'accounts')"
                :aria-describedby="trendTooltip && trendTooltip.date === bar.date && trendTooltip.kind === 'accounts' ? 'usage-trend-tooltip' : null"
                @mouseenter="showTrendTooltip($event, bar, 'accounts')"
                @mousemove="showTrendTooltip($event, bar, 'accounts')"
                @mouseleave="hideTrendTooltip"
                @focus="showTrendTooltip($event, bar, 'accounts')"
                @blur="hideTrendTooltip"
              >
              </rect>
              <text :x="bar.centerX" y="242" text-anchor="middle" class="usage-analytics__chart-axis-label">
                {{ bar.weekday }}
              </text>
              <text :x="bar.centerX" y="260" text-anchor="middle" class="usage-analytics__chart-axis-label">
                {{ bar.dateLabel }}
              </text>
            </g>
          </svg>
          <div
            v-if="trendTooltip"
            id="usage-trend-tooltip"
            class="usage-analytics__chart-tooltip"
            :style="trendTooltip.style"
            role="tooltip"
          >
            <p class="usage-analytics__chart-tooltip-date">{{ trendTooltip.dateLabel }}</p>
            <p class="usage-analytics__chart-tooltip-total">
              <span>{{ trendTooltip.title }}</span>
              <strong>{{ trendTooltip.total }}</strong>
            </p>
            <div class="usage-analytics__chart-tooltip-breakdown">
              <p>
                <span><i class="usage-analytics__tooltip-dot usage-analytics__tooltip-dot--member"></i>Miembros</span>
                <strong>{{ trendTooltip.members }}</strong>
              </p>
              <p>
                <span><i class="usage-analytics__tooltip-dot usage-analytics__tooltip-dot--submember"></i>Submiembros</span>
                <strong>{{ trendTooltip.submembers }}</strong>
              </p>
            </div>
          </div>
        </div>
        <div v-else class="usage-analytics__chart-empty">
          No se registraron ingresos en estos días. Si el periodo incluye más días, usa las flechas para revisarlos.
        </div>
      </article>

      <article class="usage-analytics__chart-card usage-analytics__chart-card--heatmap">
        <div class="usage-analytics__chart-heading usage-analytics__chart-heading--stacked">
          <div>
            <p class="usage-analytics__section-label">Días y horarios</p>
            <h2>Momentos de mayor actividad</h2>
          </div>
          <div class="usage-analytics__highlights">
            <span>Día con más ingresos <strong>{{ activityHighlights.day }}</strong></span>
            <span>Hora con más ingresos <strong>{{ activityHighlights.hour }}</strong></span>
          </div>
        </div>

        <div class="usage-analytics__heatmap" role="table" aria-label="Ingresos por día y hora">
          <div class="usage-analytics__heatmap-row usage-analytics__heatmap-row--header" role="row">
            <span role="columnheader" aria-label="Día"></span>
            <span
              v-for="hour in hours"
              :key="`hour-heading-${hour}`"
              role="columnheader"
              :aria-label="formatHour(hour)"
            >{{ hour % 2 === 0 ? formatHour(hour) : '' }}</span>
          </div>
          <div
            v-for="day in heatmapDays"
            :key="day.weekday"
            class="usage-analytics__heatmap-row"
            role="row"
          >
            <span class="usage-analytics__heatmap-day" role="rowheader">{{ day.label }}</span>
            <span
              v-for="cell in day.cells"
              :key="`${day.weekday}-${cell.hour}`"
              class="usage-analytics__heatmap-cell"
              role="cell"
              tabindex="0"
              :style="{ backgroundColor: heatmapColor(cell.totalLogins) }"
              :aria-label="heatmapCellAriaLabel(day.label, cell)"
              :aria-describedby="heatmapTooltip && heatmapTooltip.weekday === cell.weekday && heatmapTooltip.hour === cell.hour ? 'usage-heatmap-tooltip' : null"
              @mouseenter="showHeatmapTooltip($event, day.label, cell)"
              @mousemove="showHeatmapTooltip($event, day.label, cell)"
              @mouseleave="hideHeatmapTooltip"
              @focus="showHeatmapTooltip($event, day.label, cell)"
              @blur="hideHeatmapTooltip"
            ></span>
          </div>
        </div>

        <div class="usage-analytics__heatmap-legend">
          <span>Menos ingresos</span>
          <i
            v-for="step in 5"
            :key="`legend-${step}`"
            :style="{ backgroundColor: heatmapColor((heatmapMax * step) / 5) }"
          ></i>
          <span>Más ingresos</span>
        </div>
        <div
          v-if="heatmapTooltip"
          id="usage-heatmap-tooltip"
          class="usage-analytics__chart-tooltip usage-analytics__chart-tooltip--heatmap"
          :style="heatmapTooltip.style"
          role="tooltip"
        >
          <p class="usage-analytics__chart-tooltip-date">
            {{ heatmapTooltip.day }} · {{ formatHour(heatmapTooltip.hour) }}
          </p>
          <p class="usage-analytics__chart-tooltip-total">
            <span>Ingresos totales</span>
            <strong>{{ heatmapTooltip.totalLogins }}</strong>
          </p>
          <div class="usage-analytics__chart-tooltip-breakdown">
            <p>
              <span><i class="usage-analytics__tooltip-dot usage-analytics__tooltip-dot--member"></i>Miembros</span>
              <strong>{{ heatmapTooltip.memberLogins }} <small>ingresos · {{ heatmapTooltip.memberAccounts }} personas</small></strong>
            </p>
            <p>
              <span><i class="usage-analytics__tooltip-dot usage-analytics__tooltip-dot--submember"></i>Submiembros</span>
              <strong>{{ heatmapTooltip.submemberLogins }} <small>ingresos · {{ heatmapTooltip.submemberAccounts }} personas</small></strong>
            </p>
          </div>
        </div>
        <p v-if="activityHighlights.totalLogins === 0" class="usage-analytics__chart-empty">
          No se registraron ingresos en este periodo.
        </p>
      </article>
    </section>

    <section v-if="summary" class="usage-analytics__sections" aria-labelledby="usage-sections-title">
      <div class="usage-analytics__sections-heading">
        <div>
          <p class="usage-analytics__section-label">Apartados</p>
          <h2 id="usage-sections-title">Más y menos consultados</h2>
          <p class="usage-analytics__chart-subtitle">
            Actividad y personas únicas, separadas por miembros y submiembros.
          </p>
        </div>
        <div class="usage-analytics__section-sort" role="group" aria-label="Ordenar apartados">
          <button
            type="button"
            :aria-pressed="sectionSort === 'most' ? 'true' : 'false'"
            @click="sectionSort = 'most'"
          >Más consultados</button>
          <button
            type="button"
            :aria-pressed="sectionSort === 'least' ? 'true' : 'false'"
            @click="sectionSort = 'least'"
          >Menos consultados</button>
        </div>
      </div>

      <div v-if="sectionsLoading && !sectionsSummary" class="usage-analytics__loading" role="status">
        <span class="usage-analytics__spinner" aria-hidden="true"></span>
        Cargando apartados…
      </div>
      <div v-else-if="sectionsError" class="usage-analytics__feedback usage-analytics__feedback--error" role="alert">
        <span>{{ sectionsError }}</span>
        <button class="usage-analytics__retry" type="button" :disabled="loading" @click="fetchSummary">
          Reintentar
        </button>
      </div>
      <div v-else-if="sectionsSummary" class="usage-analytics__table-wrap">
        <table class="usage-analytics__sections-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Apartado</th>
              <th scope="col">Consultas registradas</th>
              <th scope="col">Miembros</th>
              <th scope="col">Submiembros</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(section, index) in sectionRows" :key="section.id">
              <td class="usage-analytics__table-rank">{{ index + 1 }}</td>
              <th scope="row">{{ section.name }}</th>
              <td class="usage-analytics__table-total">{{ formatNumber(section.totalVisits) }}</td>
              <td>
                <strong>{{ formatNumber(section.members.visits) }}</strong>
                <small>{{ formatNumber(section.members.people) }} personas únicas</small>
              </td>
              <td>
                <strong>{{ formatNumber(section.submembers.visits) }}</strong>
                <small>{{ formatNumber(section.submembers.people) }} personas únicas</small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="usage-analytics__footnote">
        Una consulta se registra una vez por persona y apartado en cada periodo de 30 minutos; las personas únicas se cuentan una vez en el periodo. Solo se incluyen actividades que podemos asociar claramente a un apartado. Datos disponibles desde {{ formatAvailableSince(sectionsSummary && sectionsSummary.dataAvailability && sectionsSummary.dataAvailability.sections) }}.
      </p>
    </section>

    <section v-if="summary" class="usage-analytics__sections usage-analytics__documents" aria-labelledby="usage-documents-title">
      <div class="usage-analytics__sections-heading">
        <div>
          <p class="usage-analytics__section-label">{{ communityLabel }}</p>
          <h2 id="usage-documents-title">Actividad de los comunicados</h2>
          <p class="usage-analytics__chart-subtitle">
            Vistas, reproducciones, descargas y respuestas de los comunicados.
          </p>
        </div>
        <div class="usage-analytics__publication-controls">
          <label class="usage-analytics__publication-control" for="usage-publication-audience">
            <span>Mostrar</span>
            <select id="usage-publication-audience" v-model="accountTypeFilter">
              <option value="both">Miembros y submiembros</option>
              <option value="members">Miembros</option>
              <option value="submembers">Submiembros</option>
            </select>
          </label>
          <label class="usage-analytics__publication-control" for="usage-publication-sort">
            <span>Ordenar por</span>
            <select id="usage-publication-sort" v-model="documentSort">
              <option value="consultations">Más vistos</option>
              <option value="downloads">Más descargados</option>
              <option value="videoPlays">Más reproducidos</option>
              <option value="responses">Más respondidos</option>
            </select>
          </label>
        </div>
      </div>

      <div v-if="documentsLoading && !documentsSummary" class="usage-analytics__loading" role="status">
        <span class="usage-analytics__spinner" aria-hidden="true"></span>
        Cargando comunicados…
      </div>
      <div v-else-if="documentsError" class="usage-analytics__feedback usage-analytics__feedback--error" role="alert">
        <span>{{ documentsError }}</span>
        <button class="usage-analytics__retry" type="button" :disabled="loading" @click="fetchSummary">
          Reintentar
        </button>
      </div>
      <div v-else-if="documentsSummary && documentRows.length" class="usage-analytics__table-wrap">
        <table class="usage-analytics__sections-table usage-analytics__documents-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Comunicado</th>
              <th scope="col">Vistas</th>
              <th scope="col">Reproducciones</th>
              <th scope="col">Descargas</th>
              <th scope="col">Respuestas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(document, index) in paginatedDocumentRows" :key="document.id">
              <td class="usage-analytics__table-rank">{{ (documentsPage - 1) * documentsPageSize + index + 1 }}</td>
              <th scope="row">
                {{ document.name }}
              </th>
              <td>
                <strong>{{ formatNumber(publicationMetric(document, 'consultations')) }}</strong>
                <small>{{ formatNumber(publicationMetric(document, 'peopleConsulted')) }} personas únicas</small>
              </td>
              <td>
                <template v-if="document.type === 'VIDEO'">
                  <strong>{{ formatNumber(publicationMetric(document, 'videoPlays')) }}</strong>
                  <small>{{ formatNumber(publicationMetric(document, 'peoplePlayed')) }} personas únicas</small>
                </template>
                <span v-else>—</span>
              </td>
              <td>
                <template v-if="document.type === 'DOC'">
                  <strong>{{ formatNumber(publicationMetric(document, 'downloads')) }}</strong>
                  <small>{{ formatNumber(publicationMetric(document, 'peopleDownloaded')) }} personas únicas</small>
                </template>
                <span v-else>—</span>
              </td>
              <td>
                <template v-if="document.type === 'POLL'">
                  <strong>{{ formatNumber(publicationMetric(document, 'responses')) }}</strong>
                  <small>{{ formatNumber(publicationMetric(document, 'peopleResponded')) }} personas únicas</small>
                </template>
                <span v-else>—</span>
              </td>
            </tr>
          </tbody>
        </table>
        <nav
          v-if="documentPageCount > 1"
          class="usage-analytics__table-pagination"
          aria-label="Páginas de comunicados"
        >
          <span>Mostrando {{ documentPageStart }}–{{ documentPageEnd }} de {{ formatNumber(documentRows.length) }} comunicados</span>
          <div>
            <button
              class="usage-analytics__table-page-button"
              type="button"
              :disabled="documentsPage <= 1"
              @click="documentsPage -= 1"
            >Anterior</button>
            <span>Página {{ documentsPage }} de {{ documentPageCount }}</span>
            <button
              class="usage-analytics__table-page-button"
              type="button"
              :disabled="documentsPage >= documentPageCount"
              @click="documentsPage += 1"
            >Siguiente</button>
          </div>
        </nav>
      </div>
      <p v-else-if="documentsSummary" class="usage-analytics__chart-empty">
        No hay comunicados publicados en {{ communityLabel }}.
      </p>
      <p class="usage-analytics__footnote">
        Una vista se registra cuando una parte del comunicado permanece visible al menos un segundo o se abre su detalle, como máximo una vez por persona y comunicado cada 30 minutos. Las reproducciones cuentan el inicio confirmado por el reproductor. Las encuestas actuales admiten una respuesta por organización, atribuida a quien la envía. Las descargas se cuentan cuando el archivo termina de descargarse desde la plataforma. Abrir o compartir una copia guardada no genera otra descarga. Datos disponibles desde {{ formatAvailableSince(documentsSummary && documentsSummary.dataAvailability && (documentsSummary.dataAvailability.publications || documentsSummary.dataAvailability.documents)) }}.
      </p>
    </section>

    <section v-if="summary" class="usage-analytics__sections usage-analytics__time" aria-labelledby="usage-time-title">
      <div class="usage-analytics__sections-heading">
        <div>
          <p class="usage-analytics__section-label">Tiempo en la plataforma</p>
          <h2 id="usage-time-title">Tiempo de permanencia estimado</h2>
          <p class="usage-analytics__chart-subtitle">
            Promedio y tiempo típico de las personas con uso registrado durante el periodo.
          </p>
        </div>
      </div>

      <div v-if="timeLoading && !timeSummary" class="usage-analytics__loading" role="status">
        <span class="usage-analytics__spinner" aria-hidden="true"></span>
        Calculando el tiempo estimado…
      </div>
      <div v-else-if="timeError" class="usage-analytics__feedback usage-analytics__feedback--error" role="alert">
        <span>{{ timeError }}</span>
        <button class="usage-analytics__retry" type="button" :disabled="loading" @click="fetchSummary">
          Reintentar
        </button>
      </div>
        <template v-else-if="timeSummary">
          <div class="usage-analytics__time-summary">
            <article class="usage-analytics__time-card">
              <div class="usage-analytics__time-primary">
                <div>
                  <p>Promedio de permanencia</p>
                  <span>Miembros con uso registrado</span>
                </div>
                <strong>{{ formatDuration(timeSummary.members.averageMinutesPerPerson) }}</strong>
              </div>
              <small>Tiempo típico: {{ formatDuration(timeSummary.members.medianMinutesPerPerson) }} · {{ formatNumber(timeSummary.members.activePeople) }} personas · {{ formatNumber(timeSummary.members.sessions) }} periodos de uso</small>
              <div class="usage-analytics__time-secondary">
                <span>Submiembros</span>
                <strong>{{ formatDuration(timeSummary.submembers.averageMinutesPerPerson) }}</strong>
                <small>Tiempo típico: {{ formatDuration(timeSummary.submembers.medianMinutesPerPerson) }} · {{ formatNumber(timeSummary.submembers.activePeople) }} personas · {{ formatNumber(timeSummary.submembers.sessions) }} periodos de uso</small>
              </div>
            </article>
          </div>

          <div v-if="timeSummary.members.activePeople || timeSummary.submembers.activePeople" class="usage-analytics__time-distribution">
          <h3>Personas según el tiempo estimado</h3>
          <div v-for="row in timeDistributionRows" :key="row.id" class="usage-analytics__time-bin">
            <strong class="usage-analytics__time-bin-label">{{ row.label }}</strong>
            <div class="usage-analytics__time-bin-group">
              <span>Miembros</span>
              <div class="usage-analytics__time-track" role="img" :aria-label="`${row.label}: ${row.members} miembros`">
                <i class="usage-analytics__time-fill" :style="{ width: `${row.membersWidth}%` }"></i>
              </div>
              <b>{{ formatNumber(row.members) }}</b>
            </div>
            <div class="usage-analytics__time-bin-group">
              <span>Submiembros</span>
              <div class="usage-analytics__time-track" role="img" :aria-label="`${row.label}: ${row.submembers} submiembros`">
                <i class="usage-analytics__time-fill usage-analytics__time-fill--submembers" :style="{ width: `${row.submembersWidth}%` }"></i>
              </div>
              <b>{{ formatNumber(row.submembers) }}</b>
            </div>
            </div>
          </div>
          <p v-else class="usage-analytics__chart-empty">
            No se observó actividad suficiente para estimar el tiempo en este periodo.
          </p>
      </template>
      <p v-if="timeSummary" class="usage-analytics__footnote">
        El tiempo es aproximado y se calcula con la actividad registrada al usar la plataforma. Esta actividad se agrupa cada {{ timeSummary.methodology.activityWindowMinutes }} min; si solo se observa una acción se estima {{ timeSummary.methodology.singleActivityEstimateMinutes }} min y entre acciones se suman hasta {{ timeSummary.methodology.maximumIntervalMinutes }} min. Si pasan {{ timeSummary.methodology.sessionClosesAfterMinutes }} min sin actividad, se cuenta un nuevo periodo de uso. El cálculo puede dejar por fuera el tiempo sin acciones y no confirma que la pantalla haya permanecido abierta. Se combina la actividad de una cuenta aunque use varias pestañas o dispositivos. Datos disponibles desde {{ formatAvailableSince(timeSummary.dataAvailability && timeSummary.dataAvailability.engagement) }}.
      </p>
    </section>

  </main>
</template>

<script>
import Vue from 'vue';
import { mapState } from 'vuex';
import { getCommunityLabel } from '@/share/communityLabel';

const TIME_ZONE = 'America/Bogota';
const TREND_DAYS_PER_PAGE = 7;
const DEFAULT_MAX_DATE_RANGE_DAYS = 90;
const DOCUMENTS_PER_PAGE = 10;
const WEEKDAYS = [
  { weekday: 1, label: 'Lunes' },
  { weekday: 2, label: 'Martes' },
  { weekday: 3, label: 'Miércoles' },
  { weekday: 4, label: 'Jueves' },
  { weekday: 5, label: 'Viernes' },
  { weekday: 6, label: 'Sábado' },
  { weekday: 7, label: 'Domingo' },
];

const formatDateInTimeZone = (date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = parts.reduce((result, { type, value }) => {
    result[type] = value;
    return result;
  }, {});
  return `${values.year}-${values.month}-${values.day}`;
};

const shiftDate = (date, days) => {
  const [year, month, day] = date.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return [
    shifted.getUTCFullYear(),
    String(shifted.getUTCMonth() + 1).padStart(2, '0'),
    String(shifted.getUTCDate()).padStart(2, '0'),
  ].join('-');
};

export default {
  name: 'MemberUsageAnalytics',

  data() {
    const todayInBogota = formatDateInTimeZone(new Date());
    return {
      todayInBogota,
      documentsPage: 1,
      documentsPageSize: DOCUMENTS_PER_PAGE,
      configuredMaximumRangeDays: DEFAULT_MAX_DATE_RANGE_DAYS,
      rangePreset: '30',
      startDate: shiftDate(todayInBogota, -29),
      endDate: todayInBogota,
      trendPage: 0,
      trendTooltip: null,
      heatmapTooltip: null,
      loading: false,
      error: null,
      summary: null,
      sectionsSummary: null,
      sectionsLoading: false,
      sectionsError: null,
      sectionSort: 'most',
      documentsSummary: null,
      documentsLoading: false,
      documentsError: null,
      documentSort: 'consultations',
      accountTypeFilter: 'both',
      timeSummary: null,
      timeLoading: false,
      timeError: null,
      exportError: null,
    };
  },

  computed: {
    ...mapState('user', ['user']),
    communityLabel() {
      return getCommunityLabel(this.user);
    },
    dateRangeInvalid() {
      return !this.startDate || !this.endDate || this.startDate > this.endDate;
    },
    selectedRangeDays() {
      if (this.dateRangeInvalid) return 0;
      const start = new Date(`${this.startDate}T00:00:00Z`).getTime();
      const end = new Date(`${this.endDate}T00:00:00Z`).getTime();
      return Math.floor((end - start) / 86400000) + 1;
    },
    maxRangeDays() {
      return this.summary && this.summary.range && this.summary.range.maxRangeDays
        ? this.summary.range.maxRangeDays
        : this.configuredMaximumRangeDays;
    },
    dateRangeTooLong() {
      return this.selectedRangeDays > this.maxRangeDays;
    },
    canExportCsv() {
      return Boolean(
        this.summary
        && this.sectionsSummary
        && this.documentsSummary
        && this.timeSummary
        && !this.loading
        && !this.sectionsLoading
        && !this.documentsLoading
        && !this.timeLoading
        && !this.error
        && !this.sectionsError
        && !this.documentsError
        && !this.timeError,
      );
    },
    sectionRows() {
      if (!this.sectionsSummary) return [];
      return [...(this.sectionsSummary.sections || [])]
        .map((section) => section.id === 'COMMUNITY'
          ? { ...section, name: this.communityLabel }
          : section)
        .sort((first, second) => {
          const difference = this.sectionSort === 'most'
            ? second.totalVisits - first.totalVisits
            : first.totalVisits - second.totalVisits;
          return difference || first.name.localeCompare(second.name, 'es');
        });
    },
    documentRows() {
      if (!this.documentsSummary) return [];
      const publications = this.documentsSummary.publications || this.documentsSummary.documents || [];
      return [...publications].sort((first, second) => {
        const difference = this.publicationMetric(second, this.documentSort)
          - this.publicationMetric(first, this.documentSort);
        return difference || first.name.localeCompare(second.name, 'es');
      });
    },
    paginatedDocumentRows() {
      const start = (this.documentsPage - 1) * this.documentsPageSize;
      return this.documentRows.slice(start, start + this.documentsPageSize);
    },
    documentPageCount() {
      return Math.max(1, Math.ceil(this.documentRows.length / this.documentsPageSize));
    },
    documentPageStart() {
      return this.documentRows.length
        ? ((this.documentsPage - 1) * this.documentsPageSize) + 1
        : 0;
    },
    documentPageEnd() {
      return Math.min(this.documentsPage * this.documentsPageSize, this.documentRows.length);
    },
    timeDistributionRows() {
      if (!this.timeSummary) return [];
      const members = this.timeSummary.members.distribution || [];
      const submembers = this.timeSummary.submembers.distribution || [];
      const maxPeople = Math.max(
        0,
        ...members.map((bin) => bin.people || 0),
        ...submembers.map((bin) => bin.people || 0),
      );
      return members.map((bin, index) => {
        const memberPeople = bin.people || 0;
        const submemberBin = submembers[index] || {};
        const submemberPeople = submemberBin.people || 0;
        return {
          id: bin.id,
          label: bin.label,
          members: memberPeople,
          submembers: submemberPeople,
          membersWidth: maxPeople ? (memberPeople / maxPeople) * 100 : 0,
          submembersWidth: maxPeople ? (submemberPeople / maxPeople) * 100 : 0,
        };
      });
    },
    metrics() {
      if (!this.summary) return [];
      const byAccountType = this.summary.byAccountType || {};
      const members = byAccountType.members || {};
      const submembers = byAccountType.submembers || {};
      const previousMembers = members.previousPeriod || {};
      const number = (value) => new Intl.NumberFormat('es-CO').format(value || 0);
      const activityRate = (accounts) => (
        accounts.activityRatePercentage == null
          ? '—'
          : `${number(accounts.activityRatePercentage)}%`
      );
      return [
        {
          label: 'Miembros que ingresaron',
          value: number(members.accountsLoggedIn),
          caption: 'Cada miembro se cuenta una vez',
          comparison: this.formatCountComparison(
            members.accountsLoggedIn || 0,
            previousMembers.accountsLoggedIn,
          ),
          secondary: {
            label: 'Submiembros que ingresaron',
            value: number(submembers.accountsLoggedIn),
          },
          icon: 'unique-login',
        },
        {
          label: 'Miembros activos',
          value: number(members.activeAccounts),
          caption: 'Miembros registrados como activos',
          comparison: null,
          secondary: {
            label: 'Submiembros activos',
            value: number(submembers.activeAccounts),
          },
          icon: 'members',
        },
        {
          label: 'Ingresos de miembros',
          value: number(members.totalLogins),
          caption: 'Accesos realizados correctamente por miembros',
          comparison: this.formatCountComparison(
            members.totalLogins || 0,
            previousMembers.totalLogins,
          ),
          secondary: {
            label: 'Ingresos de submiembros',
            value: number(submembers.totalLogins),
          },
          icon: 'logins',
        },
        {
          label: 'Actividad de miembros',
          value: activityRate(members),
          caption: 'Porcentaje de miembros activos que ingresaron',
          comparison: this.formatRateComparison(
            members.activityRatePercentage,
            previousMembers.activityRatePercentage,
          ),
          secondary: {
            label: 'Actividad de submiembros',
            value: activityRate(submembers),
          },
          icon: 'activity-rate',
        },
      ];
    },
    hours() {
      return Array.from({ length: 24 }, (_, hour) => hour);
    },
    trendRows() {
      if (!this.summary) return [];
      const activityByDate = new Map(
        (this.summary.dailyTrend || []).map((row) => [row.date, row]),
      );
      const rows = [];
      let date = this.summary.range.startDate;

      while (date <= this.summary.range.endDate) {
        const activity = activityByDate.get(date) || {};
        rows.push({
          date,
          totalLogins: activity.totalLogins || 0,
          membersLoggedIn: activity.membersLoggedIn || 0,
          memberLogins: activity.memberLogins || 0,
          submemberLogins: activity.submemberLogins || 0,
          memberAccountsLoggedIn: activity.memberAccountsLoggedIn || 0,
          submemberAccountsLoggedIn: activity.submemberAccountsLoggedIn || 0,
        });
        date = shiftDate(date, 1);
      }

      return rows;
    },
    visibleTrendRows() {
      const end = Math.max(0, this.trendRows.length - this.trendPage * TREND_DAYS_PER_PAGE);
      const start = Math.max(0, end - TREND_DAYS_PER_PAGE);
      return this.trendRows.slice(start, end);
    },
    trendPageCount() {
      return Math.max(1, Math.ceil(this.trendRows.length / TREND_DAYS_PER_PAGE));
    },
    canShowOlderTrendPage() {
      return this.trendPage < this.trendPageCount - 1;
    },
    canShowNewerTrendPage() {
      return this.trendPage > 0;
    },
    trendPeriodLabel() {
      if (!this.visibleTrendRows.length) return '';
      const first = this.visibleTrendRows[0].date;
      const last = this.visibleTrendRows[this.visibleTrendRows.length - 1].date;
      return `${this.formatDate(first)} – ${this.formatDate(last)}`;
    },
    trendChart() {
      const rows = this.visibleTrendRows;
      const maximum = Math.max(
        1,
        rows.reduce(
          (currentMaximum, row) => Math.max(currentMaximum, row.totalLogins, row.membersLoggedIn),
          0,
        ),
      );
      const left = 44;
      const right = 704;
      const top = 18;
      const bottom = 218;
      const slotWidth = rows.length ? (right - left) / rows.length : 0;
      const barWidth = Math.min(23, slotWidth * 0.26);
      const groupGap = Math.min(8, slotWidth * 0.1);
      const yForValue = (value) => bottom - (value / maximum) * (bottom - top);
      const guideValues = Array.from(new Set([maximum, Math.round(maximum / 2), 0]));

      return {
        hasActivity: rows.some((row) => row.totalLogins > 0),
        bars: rows.map((row, index) => {
          const centerX = left + slotWidth * (index + 0.5);
          const groupWidth = barWidth * 2 + groupGap;
          const loginsHeight = (row.totalLogins / maximum) * (bottom - top);
          const membersHeight = (row.membersLoggedIn / maximum) * (bottom - top);
          return {
            ...row,
            centerX,
            width: barWidth,
            loginsX: centerX - groupWidth / 2,
            membersX: centerX - groupWidth / 2 + barWidth + groupGap,
            loginsY: bottom - loginsHeight,
            membersY: bottom - membersHeight,
            loginsHeight,
            membersHeight,
            weekday: this.formatShortWeekday(row.date),
            dateLabel: this.formatShortDate(row.date),
          };
        }),
        guides: guideValues.map((value) => ({ value, y: yForValue(value), label: value })),
      };
    },
    heatmapDays() {
      if (!this.summary) return [];
      const activityByCell = new Map(
        (this.summary.activityByWeekdayHour || []).map((cell) => [
          `${cell.weekday}-${cell.hour}`,
          cell,
        ]),
      );

      return WEEKDAYS.map((day) => ({
        ...day,
        cells: this.hours.map((hour) =>
          activityByCell.get(`${day.weekday}-${hour}`) || {
            weekday: day.weekday,
            hour,
            totalLogins: 0,
            membersLoggedIn: 0,
            memberLogins: 0,
            submemberLogins: 0,
            memberAccountsLoggedIn: 0,
            submemberAccountsLoggedIn: 0,
          },
        ),
      }));
    },
    heatmapMax() {
      return this.heatmapDays.reduce(
        (maximum, day) =>
          day.cells.reduce((dayMaximum, cell) => Math.max(dayMaximum, cell.totalLogins), maximum),
        0,
      );
    },
    activityHighlights() {
      if (!this.heatmapDays.length || this.heatmapMax === 0) {
        return { day: 'Sin actividad', hour: 'Sin actividad', totalLogins: 0 };
      }

      const byHour = this.hours.map((hour) => ({
        hour,
        totalLogins: this.heatmapDays.reduce(
          (sum, day) => sum + day.cells[hour].totalLogins,
          0,
        ),
      }));
      const busiestDay = this.heatmapDays.reduce((best, day) => {
        const totalLogins = day.cells.reduce((sum, cell) => sum + cell.totalLogins, 0);
        return totalLogins > best.totalLogins ? { label: day.label, totalLogins } : best;
      }, { label: 'Sin actividad', totalLogins: 0 });
      const busiestHour = byHour.reduce(
        (best, hour) => (hour.totalLogins > best.totalLogins ? hour : best),
        { hour: null, totalLogins: 0 },
      );
      const totalLogins = byHour.reduce((sum, hour) => sum + hour.totalLogins, 0);

      return {
        day: busiestDay.label,
        hour: this.formatHour(busiestHour.hour),
        totalLogins,
      };
    },
  },

  mounted() {
    this.fetchSummary();
  },

  watch: {
    documentSort() {
      this.documentsPage = 1;
    },
    accountTypeFilter() {
      this.documentsPage = 1;
    },
    documentsSummary() {
      this.documentsPage = 1;
    },
  },

  methods: {
    publicationMetric(publication, metric) {
      const members = publication.members && publication.members[metric]
        ? publication.members[metric]
        : 0;
      const submembers = publication.submembers && publication.submembers[metric]
        ? publication.submembers[metric]
        : 0;

      if (this.accountTypeFilter === 'members') return members;
      if (this.accountTypeFilter === 'submembers') return submembers;
      return members + submembers;
    },
    selectPreset() {
      if (this.rangePreset === 'custom') return;
      this.todayInBogota = formatDateInTimeZone(new Date());
      this.endDate = this.todayInBogota;
      this.startDate = shiftDate(this.todayInBogota, -Number(this.rangePreset) + 1);
      this.fetchSummary();
    },
    formatCountComparison(current, previous) {
      if (previous == null) return 'Sin datos anteriores para comparar';
      if (previous === 0) {
        return current === 0
          ? 'Sin cambios respecto al periodo anterior'
          : 'Sin actividad en el periodo anterior';
      }

      const change = ((current - previous) / previous) * 100;
      if (change === 0) return 'Sin cambios respecto al periodo anterior';
      const direction = change > 0 ? 'Aumentó' : 'Disminuyó';
      const amount = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format(
        Math.abs(change),
      );
      return `${direction} ${amount}% frente al periodo anterior`;
    },
    formatRateComparison(current, previous) {
      if (current == null || previous == null) return 'No hay datos para comparar';
      if (previous === 0) {
        return current === 0
          ? 'Sin cambios respecto al periodo anterior'
          : 'Aumentó desde 0% en el periodo anterior';
      }

      const change = ((current - previous) / previous) * 100;
      if (change === 0) return 'Sin cambios respecto al periodo anterior';
      const direction = change > 0 ? 'Aumentó' : 'Disminuyó';
      const amount = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format(
        Math.abs(change),
      );
      return `${direction} ${amount}% frente al periodo anterior`;
    },
    formatHour(hour) {
      return `${String(hour).padStart(2, '0')}:00`;
    },
    formatShortDate(date) {
      const [, month, day] = date.split('-');
      return `${day}/${month}`;
    },
    formatShortWeekday(date) {
      const [year, month, day] = date.split('-').map(Number);
      return new Intl.DateTimeFormat('es-CO', {
        timeZone: 'UTC',
        weekday: 'short',
      })
        .format(new Date(Date.UTC(year, month - 1, day, 12)))
        .replace('.', '');
    },
    heatmapColor(logins) {
      const intensity = this.heatmapMax > 0 ? logins / this.heatmapMax : 0;
      const opacity = logins > 0 ? 0.12 + intensity * 0.78 : 0.05;
      return `rgba(50, 151, 230, ${opacity})`;
    },
    formatNumber(value) {
      return new Intl.NumberFormat('es-CO').format(value || 0);
    },
    formatPercentage(value) {
      if (value == null) return '—';
      return `${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format(value)}%`;
    },
    formatDuration(minutes) {
      if (minutes == null) return 'Sin datos';
      if (minutes < 1) return 'Menos de 1 min';
      if (minutes >= 60) {
        const roundedMinutes = Math.round(minutes);
        const hours = Math.floor(roundedMinutes / 60);
        const remainder = roundedMinutes % 60;
        return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
      }
      return `${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format(minutes)} min`;
    },
    formatDocumentDate(value) {
      if (!value) return 'fecha no disponible';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'fecha no disponible';
      return new Intl.DateTimeFormat('es-CO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: TIME_ZONE,
      }).format(date);
    },
    formatPublicationType(type) {
      return ({
        BLOG: 'Comunicado',
        VIDEO: 'Video',
        DOC: 'Documento',
        LINK: 'Enlace',
        POLL: 'Encuesta',
      })[type] || 'Comunicado';
    },
    formatAvailableSince(value) {
      return value ? this.formatDate(value) : 'sin registros';
    },
    exportCsv() {
      if (!this.canExportCsv) return;

      try {
        const rows = [[
          'Categoría',
          'Indicador',
          'Detalle',
          'Desde',
          'Hasta',
          'Miembros',
          'Submiembros',
          'Total',
          'Unidad',
        ]];
        const sum = (first, second) => (
          typeof first === 'number' ? first : 0
        ) + (typeof second === 'number' ? second : 0);
        const add = (category, metric, detail, from, to, members, submembers, unit, total) => {
          rows.push([
            category,
            metric,
            detail || '',
            from || '',
            to || '',
            members == null ? '' : members,
            submembers == null ? '' : submembers,
            total === undefined ? sum(members, submembers) : total,
            unit || '',
          ]);
        };
        const loginGroups = this.summary.byAccountType || {};
        const memberLogins = loginGroups.members || {};
        const submemberLogins = loginGroups.submembers || {};
        const previous = this.summary.previousPeriod.range;

        add('Periodo', 'Consulta', 'Hora local de Colombia', this.startDate, this.endDate, '', '', '', '');
        add('Disponibilidad', 'Ingresos', 'Datos disponibles desde', this.summary.dataAvailability.logins, '', '', '', '', '');
        add('Disponibilidad', 'Apartados', 'Datos disponibles desde', this.sectionsSummary.dataAvailability.sections, '', '', '', '', '');
        add('Disponibilidad', this.communityLabel, 'Datos disponibles desde', this.documentsSummary.dataAvailability.publications || this.documentsSummary.dataAvailability.documents, '', '', '', '', '');
        add('Disponibilidad', 'Tiempo estimado', 'Datos disponibles desde', this.timeSummary.dataAvailability.engagement, '', '', '', '', '');
        add('Ingresos', 'Cuentas activas', 'Periodo seleccionado', this.startDate, this.endDate,
          memberLogins.activeAccounts, submemberLogins.activeAccounts, 'cuentas');
        add('Ingresos', 'Personas que ingresaron', 'Periodo seleccionado', this.startDate, this.endDate,
          memberLogins.accountsLoggedIn, submemberLogins.accountsLoggedIn, 'personas');
        add('Ingresos', 'Accesos realizados', 'Periodo seleccionado', this.startDate, this.endDate,
          memberLogins.totalLogins, submemberLogins.totalLogins, 'accesos');
        add('Ingresos', 'Actividad', 'Porcentaje de cuentas activas que ingresaron', this.startDate, this.endDate,
          memberLogins.activityRatePercentage, submemberLogins.activityRatePercentage, '%', '');
        const previousMembers = memberLogins.previousPeriod || {};
        const previousSubmembers = submemberLogins.previousPeriod || {};
        add('Ingresos', 'Personas que ingresaron', 'Periodo anterior', previous.startDate, previous.endDate,
          previousMembers.accountsLoggedIn, previousSubmembers.accountsLoggedIn, 'personas');
        add('Ingresos', 'Accesos realizados', 'Periodo anterior', previous.startDate, previous.endDate,
          previousMembers.totalLogins, previousSubmembers.totalLogins, 'accesos');
        add('Ingresos', 'Actividad', 'Periodo anterior', previous.startDate, previous.endDate,
          previousMembers.activityRatePercentage, previousSubmembers.activityRatePercentage, '%', '');

        this.trendRows.forEach((day) => {
          add('Actividad por día', 'Accesos realizados', '', day.date, day.date,
            day.memberLogins, day.submemberLogins, 'accesos');
          add('Actividad por día', 'Personas que ingresaron', '', day.date, day.date,
            day.memberAccountsLoggedIn, day.submemberAccountsLoggedIn, 'personas');
        });
        this.heatmapDays.forEach((day) => {
          day.cells.forEach((cell) => {
            const detail = `${day.label}, ${this.formatHour(cell.hour)}`;
            add('Días y horarios', 'Accesos realizados', detail, this.startDate, this.endDate,
              cell.memberLogins, cell.submemberLogins, 'accesos');
            add('Días y horarios', 'Personas que ingresaron', detail, this.startDate, this.endDate,
              cell.memberAccountsLoggedIn, cell.submemberAccountsLoggedIn, 'personas');
          });
        });

        this.sectionRows.forEach((section) => {
          add('Apartados', 'Consultas registradas', section.name, this.startDate, this.endDate,
            section.members.visits, section.submembers.visits, 'consultas');
          add('Apartados', 'Personas únicas', section.name, this.startDate, this.endDate,
            section.members.people, section.submembers.people, 'personas');
        });

        this.documentRows.forEach((documentRow) => {
          const documentLabel = `${documentRow.name} (${this.formatPublicationType(documentRow.type)}, publicado ${this.formatDocumentDate(documentRow.publishedAt)})`;
          const details = [
            ['Vistas registradas', documentRow.members.consultations, documentRow.submembers.consultations, 'vistas'],
            ['Personas únicas que lo vieron', documentRow.members.peopleConsulted, documentRow.submembers.peopleConsulted, 'personas'],
            ['Reproducciones iniciadas', documentRow.members.videoPlays, documentRow.submembers.videoPlays, 'reproducciones'],
            ['Personas únicas que reprodujeron', documentRow.members.peoplePlayed, documentRow.submembers.peoplePlayed, 'personas'],
            ['Descargas completadas', documentRow.members.downloads, documentRow.submembers.downloads, 'descargas'],
            ['Personas únicas que descargaron', documentRow.members.peopleDownloaded, documentRow.submembers.peopleDownloaded, 'personas'],
            ['Respuestas enviadas', documentRow.members.responses, documentRow.submembers.responses, 'respuestas'],
            ['Personas únicas que respondieron', documentRow.members.peopleResponded, documentRow.submembers.peopleResponded, 'personas'],
          ];
          details.forEach(([metric, members, submembers, unit]) => {
            add(this.communityLabel, metric, documentLabel, this.startDate, this.endDate,
              members, submembers, unit);
          });
        });

        const timeGroups = [
          ['Miembros', this.timeSummary.members],
          ['Submiembros', this.timeSummary.submembers],
        ];
        const timeMetrics = [
          ['Tiempo total estimado', 'totalMinutes', 'minutos', true],
          ['Promedio por persona', 'averageMinutesPerPerson', 'minutos', false],
          ['Tiempo típico por persona', 'medianMinutesPerPerson', 'minutos', false],
          ['Personas con uso registrado', 'activePeople', 'personas', true],
          ['Periodos de uso', 'sessions', 'periodos', true],
        ];
        timeMetrics.forEach(([label, key, unit, includeTotal]) => {
          const values = timeGroups.map(([, group]) => group[key]);
          const total = includeTotal ? sum(values[0], values[1]) : '';
          add('Tiempo estimado', label, '', this.startDate, this.endDate,
            values[0], values[1], unit, total);
        });
        const memberDistribution = this.timeSummary.members.distribution || [];
        const submemberDistribution = this.timeSummary.submembers.distribution || [];
        memberDistribution.forEach((bin, index) => {
          const submemberBin = submemberDistribution.find((item) => item.id === bin.id) || {};
          add('Tiempo estimado', 'Personas por duración', bin.label, this.startDate, this.endDate,
            bin.people, submemberBin.people, 'personas');
        });

        const escapeCell = (value) => {
          let cell = value == null ? '' : String(value);
          if (/^[\u0000-\u0020]*[=+\-@]/.test(cell)) cell = `'${cell}`;
          return `"${cell.replace(/"/g, '""')}"`;
        };
        const csv = `\uFEFF${rows.map((row) => row.map(escapeCell).join(';')).join('\r\n')}`;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = `analiticas-uso-${this.startDate}-${this.endDate}.csv`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.setTimeout(() => URL.revokeObjectURL(url), 0);
        this.exportError = null;
      } catch (error) {
        void error;
        this.exportError = 'No pudimos preparar el archivo. Vuelve a intentarlo.';
      }
    },
    tooltipPosition(event, containerSelector, tooltipWidth, tooltipHeight) {
      const container = event.currentTarget.closest(containerSelector);
      if (!container) return { left: '8px', top: '8px' };

      const bounds = container.getBoundingClientRect();
      const target = event.currentTarget.getBoundingClientRect();
      const width = Math.min(tooltipWidth, Math.max(0, bounds.width - 16));
      const hasPointer = event.type === 'mouseenter' || event.type === 'mousemove';
      const x = hasPointer
        ? event.clientX - bounds.left
        : target.left - bounds.left + target.width / 2;
      const y = hasPointer
        ? event.clientY - bounds.top
        : target.top - bounds.top + target.height / 2;

      let left = hasPointer ? x + 14 : x - width / 2;
      let top = hasPointer ? y + 14 : y - tooltipHeight / 2;
      if (top + tooltipHeight > bounds.height - 8) top = y - tooltipHeight - 12;

      left = Math.max(8, Math.min(left, bounds.width - width - 8));
      top = Math.max(8, Math.min(top, bounds.height - tooltipHeight - 8));
      return { left: `${left}px`, top: `${top}px` };
    },
    trendBarAriaLabel(bar, kind) {
      const isLogins = kind === 'logins';
      const total = isLogins ? bar.totalLogins : bar.membersLoggedIn;
      const members = isLogins ? bar.memberLogins : bar.memberAccountsLoggedIn;
      const submembers = isLogins ? bar.submemberLogins : bar.submemberAccountsLoggedIn;
      const label = isLogins ? 'ingresos' : 'personas que ingresaron';
      return `${this.formatDate(bar.date)}. ${this.formatNumber(total)} ${label} en total: ${this.formatNumber(members)} miembros y ${this.formatNumber(submembers)} submiembros.`;
    },
    showTrendTooltip(event, bar, kind) {
      const isLogins = kind === 'logins';
      this.heatmapTooltip = null;
      this.trendTooltip = {
        kind,
        date: bar.date,
        dateLabel: `${bar.weekday} ${this.formatDate(bar.date)}`,
        title: isLogins ? 'Ingresos totales' : 'Personas que ingresaron',
        total: this.formatNumber(isLogins ? bar.totalLogins : bar.membersLoggedIn),
        members: this.formatNumber(
          isLogins ? bar.memberLogins : bar.memberAccountsLoggedIn,
        ),
        submembers: this.formatNumber(
          isLogins ? bar.submemberLogins : bar.submemberAccountsLoggedIn,
        ),
        style: this.tooltipPosition(event, '.usage-analytics__trend-chart', 224, 128),
      };
    },
    hideTrendTooltip() {
      this.trendTooltip = null;
    },
    heatmapCellAriaLabel(day, cell) {
      return `${day}, ${this.formatHour(cell.hour)}. ${this.formatNumber(cell.totalLogins)} ingresos en total: ${this.formatNumber(cell.memberLogins)} de miembros y ${this.formatNumber(cell.submemberLogins)} de submiembros. Personas únicas: ${this.formatNumber(cell.memberAccountsLoggedIn)} miembros y ${this.formatNumber(cell.submemberAccountsLoggedIn)} submiembros.`;
    },
    showHeatmapTooltip(event, day, cell) {
      this.trendTooltip = null;
      this.heatmapTooltip = {
        day,
        weekday: cell.weekday,
        hour: cell.hour,
        totalLogins: this.formatNumber(cell.totalLogins),
        memberLogins: this.formatNumber(cell.memberLogins),
        submemberLogins: this.formatNumber(cell.submemberLogins),
        memberAccounts: this.formatNumber(cell.memberAccountsLoggedIn),
        submemberAccounts: this.formatNumber(cell.submemberAccountsLoggedIn),
        style: this.tooltipPosition(event, '.usage-analytics__chart-card--heatmap', 250, 156),
      };
    },
    hideHeatmapTooltip() {
      this.heatmapTooltip = null;
    },
    async fetchSummary() {
      if (this.dateRangeInvalid || this.dateRangeTooLong) return;
      this.trendPage = 0;
      this.documentsPage = 1;
      this.trendTooltip = null;
      this.heatmapTooltip = null;
      this.loading = true;
      this.sectionsLoading = true;
      this.documentsLoading = true;
      this.timeLoading = true;
      this.error = null;
      this.sectionsError = null;
      this.documentsError = null;
      this.timeError = null;
      this.exportError = null;
      this.sectionsSummary = null;
      this.documentsSummary = null;
      this.timeSummary = null;

      try {
        const params = { startDate: this.startDate, endDate: this.endDate };
        const [summaryResult, sectionsResult, documentsResult, timeResult] = await Promise.all([
          Vue.axios({ method: 'GET', url: '/member-analytics/login-summary', params })
            .then((response) => ({ response }), (requestError) => ({ error: requestError })),
          Vue.axios({ method: 'GET', url: '/member-analytics/sections-summary', params })
            .then((response) => ({ response }), (requestError) => ({ error: requestError })),
          Vue.axios({ method: 'GET', url: '/member-analytics/publications-summary', params })
            .then((response) => ({ response }), (requestError) => ({ error: requestError })),
          Vue.axios({ method: 'GET', url: '/member-analytics/time-summary', params })
            .then((response) => ({ response }), (requestError) => ({ error: requestError })),
        ]);

        if (summaryResult.error) {
          const failure = summaryResult.error.response && summaryResult.error.response.data;
          if (failure && Number.isInteger(failure.maxRangeDays) && failure.maxRangeDays > 0) {
            this.configuredMaximumRangeDays = failure.maxRangeDays;
            if (this.selectedRangeDays > failure.maxRangeDays) this.rangePreset = 'custom';
          }
          throw summaryResult.error;
        }
        this.summary = summaryResult.response.data;
        this.configuredMaximumRangeDays = this.summary.range.maxRangeDays || this.configuredMaximumRangeDays;

        if (sectionsResult.error) {
          this.sectionsSummary = null;
          this.sectionsError = 'No pudimos cargar el uso de los apartados. Vuelve a intentarlo.';
        } else {
          this.sectionsSummary = sectionsResult.response.data;
        }

        if (documentsResult.error) {
          this.documentsSummary = null;
          this.documentsError = `No pudimos cargar los comunicados de ${this.communityLabel}. Vuelve a intentarlo.`;
        } else {
          this.documentsSummary = documentsResult.response.data;
        }

        if (timeResult.error) {
          this.timeSummary = null;
          this.timeError = 'No pudimos calcular el tiempo de permanencia. Vuelve a intentarlo.';
        } else {
          this.timeSummary = timeResult.response.data;
        }
      } catch (error) {
        this.summary = null;
        this.error = this.dateRangeTooLong
          ? null
          : 'No pudimos cargar la información. Vuelve a intentarlo.';
      } finally {
        this.loading = false;
        this.sectionsLoading = false;
        this.documentsLoading = false;
        this.timeLoading = false;
      }
    },
    showOlderTrendPage() {
      if (this.canShowOlderTrendPage) this.trendPage += 1;
    },
    showNewerTrendPage() {
      if (this.canShowNewerTrendPage) this.trendPage -= 1;
    },
    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.usage-analytics {
  --analytics-ink: #1b273e;
  --analytics-muted: #7d8796;
  --analytics-blue: #3297e6;
  --analytics-cyan: #20b4d2;
  --analytics-cyan-soft: #e6f8fb;
  --analytics-border: #e4e9f0;
  --analytics-surface: #fff;
  --analytics-canvas: #f6f8fb;
  --analytics-danger: #b54747;
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  padding: clamp(20px, 3vw, 40px);
  color: var(--analytics-ink);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.usage-analytics__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 12px;
}

.usage-analytics__breadcrumb,
.usage-analytics__section-label {
  margin: 0 0 8px;
  color: var(--analytics-muted);
  font-size: 12px;
  letter-spacing: .03em;
}

.usage-analytics__breadcrumb span {
  padding: 0 5px;
  color: #b2bac5;
}

.usage-analytics h1,
.usage-analytics h2,
.usage-analytics p {
  margin-top: 0;
}

.usage-analytics h1 {
  margin-bottom: 5px;
  color: var(--analytics-ink);
  font-size: clamp(26px, 2.4vw, 34px);
  font-weight: 750;
  letter-spacing: -.035em;
  line-height: 1.15;
}

.usage-analytics__subtitle {
  margin-bottom: 0;
  color: var(--analytics-muted);
  font-size: 14px;
}

.usage-analytics__filters {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 9px;
  padding: 10px;
  border: 1px solid var(--analytics-border);
  border-radius: 12px;
  background: var(--analytics-surface);
  box-shadow: 0 2px 7px rgb(25 44 70 / 4%);
}

.usage-analytics__date-field {
  display: grid;
  gap: 5px;
  color: var(--analytics-muted);
  font-size: 11px;
  font-weight: 650;
}

.usage-analytics__preset-field {
  display: grid;
  gap: 5px;
  color: var(--analytics-muted);
  font-size: 11px;
  font-weight: 650;
}

.usage-analytics__date-field input,
.usage-analytics__preset-field select {
  min-width: 142px;
  height: 38px;
  padding: 0 9px;
  border: 1px solid var(--analytics-border);
  border-radius: 8px;
  background: #fff;
  color: var(--analytics-ink);
  font: inherit;
  font-size: 12px;
}

.usage-analytics__preset-field select {
  min-width: 154px;
  cursor: pointer;
}

.usage-analytics__button {
  min-height: 38px;
  padding: 0 13px;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 650;
  transition: background-color .15s ease, border-color .15s ease, transform .15s ease;
}

.usage-analytics__button:not(:disabled):hover {
  transform: translateY(-1px);
}

.usage-analytics__button--primary {
  background: var(--analytics-ink);
  color: #fff;
}

.usage-analytics__button--primary:not(:disabled):hover {
  background: #263956;
}

.usage-analytics__button--secondary {
  border-color: var(--analytics-border);
  background: #fff;
  color: var(--analytics-ink);
}

.usage-analytics__button--secondary:not(:disabled):hover {
  border-color: #c5d5e5;
  background: #f8fafc;
}

.usage-analytics__export-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-left: auto;
}

.usage-analytics__export-button svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.usage-analytics__button:disabled,
.usage-analytics__retry:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.usage-analytics__validation {
  margin: 0 0 20px;
  color: var(--analytics-danger);
  font-size: 12px;
}

.usage-analytics__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.usage-metric {
  display: flex;
  min-width: 0;
  min-height: 116px;
  align-items: flex-start;
  gap: 14px;
  padding: 19px 17px;
  border: 1px solid var(--analytics-border);
  border-radius: 12px;
  background: var(--analytics-surface);
  box-shadow: 0 2px 8px rgb(25 44 70 / 5%);
}

.usage-metric__icon {
  display: grid;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  place-items: center;
  border-radius: 10px;
  background: var(--analytics-cyan-soft);
  color: #188bac;
}

.usage-metric__icon svg {
  width: 25px;
  height: 25px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.usage-metric__content {
  min-width: 0;
}

.usage-metric__label {
  margin: 1px 0 4px;
  color: var(--analytics-muted);
  font-size: 12px;
  line-height: 1.4;
}

.usage-metric__value {
  display: block;
  color: var(--analytics-ink);
  font-size: clamp(23px, 2vw, 29px);
  font-weight: 750;
  letter-spacing: -.035em;
  line-height: 1.1;
}

.usage-metric__caption {
  margin: 5px 0 0;
  color: var(--analytics-muted);
  font-size: 11px;
  line-height: 1.4;
}

.usage-metric__secondary {
  margin-top: 9px;
  padding-top: 8px;
  border-top: 1px solid #edf0f4;
  color: var(--analytics-muted);
  font-size: 10px;
  line-height: 1.4;
}

.usage-metric__secondary p {
  margin: 0;
}

.usage-metric__secondary p + p {
  margin-top: 2px;
}

.usage-metric__secondary strong {
  margin-left: 5px;
  color: var(--analytics-ink);
  font-size: 12px;
  font-weight: 700;
}

.usage-metric__comparison {
  margin: 8px 0 0;
  color: #53677f;
  font-size: 10px;
  font-weight: 650;
  line-height: 1.45;
}

.usage-analytics__charts {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
  gap: 16px;
  margin-top: 18px;
}

.usage-analytics__chart-card {
  min-width: 0;
  padding: 20px;
  border: 1px solid var(--analytics-border);
  border-radius: 12px;
  background: var(--analytics-surface);
  box-shadow: 0 2px 8px rgb(25 44 70 / 4%);
}

.usage-analytics__chart-card--heatmap {
  position: relative;
}

.usage-analytics__chart-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.usage-analytics__chart-heading h2 {
  margin: 0 0 5px;
  font-size: 16px;
}

.usage-analytics__chart-subtitle {
  margin: 0;
  color: var(--analytics-muted);
  font-size: 11px;
}

.usage-analytics__trend-navigation {
  display: flex;
  align-items: center;
  gap: 7px;
  padding-top: 2px;
}

.usage-analytics__trend-period {
  margin-right: 3px;
  color: #53677f;
  font-size: 11px;
  font-weight: 650;
  white-space: nowrap;
}

.usage-analytics__page-button {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border: 1px solid var(--analytics-border);
  border-radius: 8px;
  background: #fff;
  color: var(--analytics-ink);
  cursor: pointer;
}

.usage-analytics__page-button:not(:disabled):hover {
  border-color: #b8cee2;
  background: #f4f8fc;
}

.usage-analytics__page-button svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.usage-analytics__page-button:disabled {
  color: #b8c1cc;
  cursor: not-allowed;
  opacity: .6;
}

.usage-analytics__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 4px;
  color: var(--analytics-muted);
  font-size: 10px;
  white-space: nowrap;
}

.usage-analytics__legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.usage-analytics__legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.usage-analytics__legend-dot--logins {
  background: var(--analytics-blue);
}

.usage-analytics__legend-dot--members {
  background: var(--analytics-cyan);
}

.usage-analytics__trend-chart svg {
  display: block;
  width: 100%;
  overflow: visible;
}

.usage-analytics__trend-chart {
  position: relative;
}

.usage-analytics__chart-gridline {
  stroke: #e9eef4;
  stroke-dasharray: 3 4;
  stroke-width: 1;
}

.usage-analytics__chart-axis-label {
  fill: var(--analytics-muted);
  font-family: inherit;
  font-size: 10px;
}

.usage-analytics__bar--logins {
  fill: var(--analytics-blue);
}

.usage-analytics__bar--members {
  fill: var(--analytics-cyan);
}

.usage-analytics__bar {
  cursor: pointer;
}

.usage-analytics__bar:focus-visible {
  outline: none;
  stroke: var(--analytics-ink);
  stroke-width: 2;
}

.usage-analytics__chart-tooltip {
  position: absolute;
  z-index: 5;
  width: 224px;
  max-width: calc(100% - 16px);
  padding: 11px 13px;
  border: 1px solid #dce5ef;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 24px rgb(25 44 70 / 16%);
  color: var(--analytics-ink);
  pointer-events: none;
}

.usage-analytics__chart-tooltip--heatmap {
  width: 250px;
}

.usage-analytics__chart-tooltip-date {
  margin: 0 0 7px;
  color: var(--analytics-muted);
  font-size: 10px;
  font-weight: 650;
}

.usage-analytics__chart-tooltip-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  font-size: 11px;
}

.usage-analytics__chart-tooltip-total span {
  color: #53677f;
}

.usage-analytics__chart-tooltip-total strong {
  color: var(--analytics-ink);
  font-size: 17px;
}

.usage-analytics__chart-tooltip-breakdown {
  display: grid;
  gap: 5px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #edf0f4;
}

.usage-analytics__chart-tooltip-breakdown p {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  font-size: 10px;
}

.usage-analytics__chart-tooltip-breakdown p > span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #53677f;
}

.usage-analytics__chart-tooltip-breakdown strong {
  color: var(--analytics-ink);
  text-align: right;
}

.usage-analytics__chart-tooltip-breakdown small {
  display: block;
  color: var(--analytics-muted);
  font-size: 9px;
  font-weight: 500;
}

.usage-analytics__tooltip-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  border-radius: 50%;
}

.usage-analytics__tooltip-dot--member {
  background: var(--analytics-blue);
}

.usage-analytics__tooltip-dot--submember {
  background: var(--analytics-cyan);
}

.usage-analytics__chart-empty {
  margin: 12px 0 0;
  color: var(--analytics-muted);
  font-size: 11px;
}

.usage-analytics__highlights {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.usage-analytics__highlights span {
  display: grid;
  gap: 3px;
  padding: 7px 9px;
  border-radius: 8px;
  background: #f2f8fd;
  color: var(--analytics-muted);
  font-size: 9px;
}

.usage-analytics__highlights strong {
  color: var(--analytics-ink);
  font-size: 11px;
}

.usage-analytics__heatmap {
  display: grid;
  gap: 6px;
}

.usage-analytics__heatmap-row {
  display: grid;
  grid-template-columns: 58px repeat(24, minmax(0, 1fr));
  align-items: center;
  gap: 3px;
}

.usage-analytics__heatmap-row--header {
  margin-bottom: 2px;
  color: var(--analytics-muted);
  font-size: 8px;
  text-align: center;
}

.usage-analytics__heatmap-day {
  color: #58677b;
  font-size: 10px;
}

.usage-analytics__heatmap-cell {
  display: block;
  height: 17px;
  border-radius: 3px;
  cursor: pointer;
}

.usage-analytics__heatmap-cell:focus-visible {
  outline: 2px solid var(--analytics-ink);
  outline-offset: 2px;
}

.usage-analytics__heatmap-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  margin-top: 15px;
  color: var(--analytics-muted);
  font-size: 9px;
}

.usage-analytics__heatmap-legend i {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.usage-analytics__sections {
  margin-top: 18px;
  padding: 21px;
  border: 1px solid var(--analytics-border);
  border-radius: 12px;
  background: var(--analytics-surface);
  box-shadow: 0 2px 8px rgb(25 44 70 / 4%);
}

.usage-analytics__sections-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.usage-analytics__sections-heading h2 {
  margin-bottom: 5px;
}

.usage-analytics__section-sort {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 3px;
  padding: 3px;
  border: 1px solid var(--analytics-border);
  border-radius: 9px;
  background: #f6f8fb;
}

.usage-analytics__section-sort button {
  padding: 7px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #53677f;
  cursor: pointer;
  font: inherit;
  font-size: 10px;
  font-weight: 650;
}

.usage-analytics__section-sort button[aria-pressed="true"] {
  background: #fff;
  color: var(--analytics-ink);
  box-shadow: 0 1px 4px rgb(25 44 70 / 12%);
}

.usage-analytics__publication-controls {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-end;
  gap: 10px;
}

.usage-analytics__publication-control {
  display: grid;
  min-width: 154px;
  gap: 5px;
  color: var(--analytics-muted);
  font-size: 10px;
  font-weight: 650;
}

.usage-analytics__publication-control select {
  width: 100%;
  min-height: 36px;
  padding: 0 30px 0 10px;
  border: 1px solid var(--analytics-border);
  border-radius: 8px;
  background: #fff;
  color: var(--analytics-ink);
  cursor: pointer;
  font: inherit;
  font-size: 11px;
}

.usage-analytics__table-wrap {
  overflow-x: auto;
}

.usage-analytics__table-pagination {
  display: flex;
  min-width: 1000px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 2px 0;
  color: var(--analytics-muted);
  font-size: 10px;
}

.usage-analytics__table-pagination > div {
  display: flex;
  align-items: center;
  gap: 9px;
  white-space: nowrap;
}

.usage-analytics__table-page-button {
  min-height: 30px;
  padding: 0 9px;
  border: 1px solid var(--analytics-border);
  border-radius: 7px;
  background: #fff;
  color: var(--analytics-ink);
  cursor: pointer;
  font: inherit;
}

.usage-analytics__table-page-button:not(:disabled):hover {
  border-color: #b8cee2;
  background: #f4f8fc;
}

.usage-analytics__table-page-button:disabled {
  color: #b8c1cc;
  cursor: not-allowed;
  opacity: .6;
}

.usage-analytics__sections-table {
  width: 100%;
  min-width: 650px;
  border-collapse: separate;
  border-spacing: 0;
  color: var(--analytics-ink);
  font-size: 11px;
  text-align: left;
}

.usage-analytics__sections-table thead th {
  padding: 10px 12px;
  background: #f5f8fb;
  color: #647187;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
}

.usage-analytics__sections-table thead th:first-child {
  border-radius: 7px 0 0 7px;
}

.usage-analytics__sections-table thead th:last-child {
  border-radius: 0 7px 7px 0;
}

.usage-analytics__sections-table tbody td,
.usage-analytics__sections-table tbody th {
  padding: 11px 12px;
  border-bottom: 1px solid #edf0f4;
}

.usage-analytics__sections-table tbody tr:last-child td,
.usage-analytics__sections-table tbody tr:last-child th {
  border-bottom: 0;
}

.usage-analytics__sections-table tbody th {
  font-size: 11px;
  font-weight: 700;
}

.usage-analytics__table-rank {
  width: 38px;
  color: var(--analytics-muted);
  text-align: center;
}

.usage-analytics__table-total {
  font-size: 13px;
  font-weight: 750;
}

.usage-analytics__sections-table tbody td strong {
  font-size: 11px;
}

.usage-analytics__sections-table tbody td small {
  display: block;
  margin-top: 3px;
  color: var(--analytics-muted);
  font-size: 9px;
}

.usage-analytics__documents-table {
  min-width: 1000px;
}

.usage-analytics__documents-table tbody th {
  min-width: 230px;
  max-width: 360px;
}

.usage-analytics__document-date {
  font-weight: 450;
  text-transform: none;
}

.usage-analytics__time-summary {
  display: grid;
  grid-template-columns: 1fr;
}

.usage-analytics__time-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid var(--analytics-border);
  border-radius: 10px;
  background: #f8fbfe;
}

.usage-analytics__time-card p,
.usage-analytics__time-card span,
.usage-analytics__time-card small {
  margin: 0;
}

.usage-analytics__time-primary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.usage-analytics__time-primary p {
  color: #56667d;
  font-size: 11px;
  font-weight: 700;
}

.usage-analytics__time-primary strong {
  color: var(--analytics-ink);
  font-size: 25px;
  line-height: 1.2;
}

.usage-analytics__time-card small {
  color: var(--analytics-muted);
  font-size: 10px;
}

.usage-analytics__time-primary span {
  display: block;
  margin-top: 3px;
  color: var(--analytics-muted);
  font-size: 10px;
}

.usage-analytics__time-secondary {
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #effafd;
}

.usage-analytics__time-secondary > span {
  color: #52677b;
  font-size: 10px;
  font-weight: 650;
}

.usage-analytics__time-secondary > strong {
  color: #167f99;
  font-size: 14px;
}

.usage-analytics__time-secondary > small {
  justify-self: end;
}

.usage-analytics__time-distribution {
  margin-top: 20px;
}

.usage-analytics__time-distribution h3 {
  margin: 0 0 12px;
  color: var(--analytics-ink);
  font-size: 12px;
}

.usage-analytics__time-bin {
  display: grid;
  grid-template-columns: minmax(145px, 0.75fr) repeat(2, minmax(0, 1fr));
  align-items: center;
  gap: 15px;
  padding: 10px 0;
  border-bottom: 1px solid #edf0f4;
}

.usage-analytics__time-bin:last-child {
  border-bottom: 0;
}

.usage-analytics__time-bin-label {
  color: #53677f;
  font-size: 10px;
}

.usage-analytics__time-bin-group {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 7px;
  color: #69778a;
  font-size: 9px;
}

.usage-analytics__time-bin-group b {
  color: var(--analytics-ink);
  font-size: 10px;
  text-align: right;
}

.usage-analytics__time-track {
  height: 8px;
  overflow: hidden;
  border-radius: 8px;
  background: #e9edf3;
}

.usage-analytics__time-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #3297e6;
}

.usage-analytics__time-fill--submembers {
  background: #20b4d2;
}

.usage-analytics__section-label {
  margin-bottom: 4px;
  color: var(--analytics-blue);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: .1em;
  text-transform: uppercase;
}

.usage-analytics h2 {
  margin-bottom: 0;
  font-size: 17px;
  font-weight: 720;
  letter-spacing: -.02em;
}

.usage-analytics__footnote {
  margin: 15px 0 0;
  color: var(--analytics-muted);
  font-size: 11px;
}

.usage-analytics__feedback,
.usage-analytics__loading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
  padding: 12px 14px;
  border-radius: 9px;
  font-size: 12px;
}

.usage-analytics__feedback {
  justify-content: space-between;
}

.usage-analytics__feedback--error {
  border: 1px solid #f0d1d1;
  background: #fff8f8;
  color: var(--analytics-danger);
}

.usage-analytics__retry {
  padding: 5px 8px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
}

.usage-analytics__loading {
  color: var(--analytics-muted);
}

.usage-analytics__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #dce7f0;
  border-top-color: var(--analytics-blue);
  border-radius: 50%;
  animation: analytics-spin .8s linear infinite;
}

.usage-analytics input:focus-visible,
.usage-analytics select:focus-visible,
.usage-analytics button:focus-visible {
  outline: 3px solid rgb(50 151 230 / 35%);
  outline-offset: 2px;
}

@keyframes analytics-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 1080px) {
  .usage-analytics__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .usage-analytics__filters {
    width: 100%;
    flex-wrap: wrap;
  }
}

@media (max-width: 820px) {
  .usage-analytics__metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .usage-analytics__charts {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .usage-analytics__filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .usage-analytics__export-button {
    margin-left: 0;
  }
}

@media (max-width: 560px) {
  .usage-analytics {
    padding: 20px 14px;
  }

  .usage-analytics__filters {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .usage-analytics__date-field input {
    width: 100%;
    min-width: 0;
  }

  .usage-analytics__preset-field select {
    width: 100%;
    min-width: 0;
  }

  .usage-analytics__button {
    width: 100%;
  }

  .usage-analytics__metrics {
    grid-template-columns: 1fr;
  }

  .usage-analytics__chart-card {
    padding: 16px 12px;
  }

  .usage-analytics__chart-heading {
    flex-direction: column;
  }

  .usage-analytics__sections {
    padding: 16px 12px;
  }

  .usage-analytics__time-secondary {
    grid-template-columns: auto auto;
  }

  .usage-analytics__time-secondary > small {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .usage-analytics__time-bin {
    grid-template-columns: 1fr;
    gap: 7px;
  }

  .usage-analytics__time-bin-group {
    grid-template-columns: 82px minmax(0, 1fr) 28px;
  }

  .usage-analytics__sections-heading {
    flex-direction: column;
  }

  .usage-analytics__publication-controls {
    width: 100%;
  }

  .usage-analytics__publication-control {
    min-width: 0;
    flex: 1;
  }

  .usage-analytics__section-sort {
    width: 100%;
  }

  .usage-analytics__section-sort button {
    flex: 1;
  }

  .usage-analytics__chart-heading--stacked {
    gap: 10px;
  }

  .usage-analytics__trend-navigation {
    width: 100%;
    justify-content: flex-start;
  }

  .usage-analytics__highlights {
    justify-content: flex-start;
  }

  .usage-analytics__heatmap-row {
    grid-template-columns: 52px repeat(24, minmax(0, 1fr));
    gap: 2px;
  }

  .usage-analytics__heatmap-day {
    font-size: 9px;
  }

  .usage-analytics__heatmap-cell {
    height: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .usage-analytics__spinner {
    animation-duration: 2s;
  }

  .usage-analytics__button {
    transition: none;
  }
}
</style>
